import React, {useEffect, useState} from 'react';
import _ from 'lodash';

import Highlighted from './Highlighted';
import {
  TYPE_ORDER, TYPE_LABELS, ResultIcon,
} from './resultTypes';
import {FadeOut} from '../FadeOut';
import {ListAskedAt} from '../ListAskedAt';
import Link from '../Link';
import {useAppContext, useAnalytics} from '../../hooks';
import {shouldHideCoaching, cx} from '../../util';

const PREVIEW_LIMIT = 6;

// Render a match-highlighted field from a result's `matches` array.
const highlight = (text, matches, key, maxCharPadding) => (
  <Highlighted text={text} match={_.find(matches, {key})} maxCharPadding={maxCharPadding} />
);

// Shared result row template
const ResultRow = ({
  iconType, image, href, clickType, item, title, subtitle, onClick,
}) => (
  <Link
    href={href}
    onClick={() => onClick({type: clickType, href, item})}
    className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-gray-50"
  >
    <ResultIcon type={iconType} image={image} />
    <div className="min-w-0 flex-1">
      <div className="truncate text-base font-medium text-gray-800">{title}</div>
      {subtitle && (
        <div className="truncate text-sm leading-5 text-gray-500">{subtitle}</div>
      )}
    </div>
  </Link>
);

const TagResult = ({result: {item}, onClick}) => {
  const isCompany = item.type === 'company';
  return (
    <ResultRow
      iconType={isCompany ? 'company' : 'role'}
      image={item.image_url}
      href={`/${isCompany ? 'companies' : 'roles'}/${item.id}`}
      clickType="tag"
      item={item}
      title={item.name}
      onClick={onClick}
    />
  );
};

const CourseResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="course"
    href={`/courses/${item.slug}`}
    clickType={item.contentType === 'track' ? 'track' : 'course'}
    item={item}
    title={highlight(item.title, matches, 'title')}
    subtitle={highlight(item.description, matches, 'description', 60)}
    onClick={onClick}
  />
);

const LessonResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="lesson"
    href={`/courses/${item.course}/${item.id}`}
    clickType="lesson"
    item={item}
    title={highlight(item.title, matches, 'title')}
    subtitle={item.courseTitle}
    onClick={onClick}
  />
);

const ResourceResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="resource"
    href={`/resources/${item.id}`}
    clickType="resource"
    item={item}
    title={highlight(item.title, matches, 'title')}
    onClick={onClick}
  />
);

const QuestionResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="question"
    href={`/questions/${item.id}`}
    clickType="question"
    item={item}
    title={highlight(item.data.title, matches, 'data.title', 100)}
    subtitle={<ListAskedAt tags={item.companies} lastAskedOn={item.last_asked_on} />}
    onClick={onClick}
  />
);

const CoachResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="coach"
    href={`/coach/${item.slug}`}
    clickType="coach"
    item={item}
    title={(
      <span>
        {highlight(item.Name, matches, 'Name')}
        {', '}
        {highlight(item.Title, matches, 'Title')}
      </span>
    )}
    subtitle={highlight(item['Brief Bio'], matches, 'Brief Bio', 60)}
    onClick={onClick}
  />
);

const BlogResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="blog"
    href={`/blog/${item.slug}`}
    clickType="blog"
    item={item}
    title={highlight(item.title, matches, 'title')}
    subtitle={highlight(item.excerpt, matches, 'excerpt', 60)}
    onClick={onClick}
  />
);

const GuideResult = ({result: {item, matches}, onClick}) => (
  <ResultRow
    iconType="guide"
    href={`/guides/${item.slug}`}
    clickType="guide"
    item={item}
    title={highlight(item.name, matches, 'name')}
    subtitle={highlight(item.intro, matches, 'intro', 60)}
    onClick={onClick}
  />
);

const RESULT_SOURCES = [
  {key: 'tags', filterType: (item) => (item.type === 'company' ? 'company' : 'role'), Component: TagResult},
  {key: 'courses', filterType: 'course', Component: CourseResult},
  {key: 'lessons', filterType: 'lesson', Component: LessonResult},
  {key: 'resources', filterType: 'resource', Component: ResourceResult},
  {key: 'questions', filterType: 'question', Component: QuestionResult},
  {key: 'coaches', filterType: 'coach', Component: CoachResult},
  {key: 'blogPosts', filterType: 'blog', Component: BlogResult},
  {key: 'guides', filterType: 'guide', Component: GuideResult},
];

// Flatten the grouped API response into a single list of rows, each tagged
// with its filterType (for pills + grouping) and its render component.
const buildRows = (results, hideCoaching) => RESULT_SOURCES.flatMap(({
  key, filterType, Component,
}) => {
  if (hideCoaching && filterType === 'coach') return [];
  return (results[key] || []).map((result) => ({
    filterType: typeof filterType === 'function' ? filterType(result.item) : filterType,
    Component,
    result,
  }));
});

const FilterPill = ({active, onClick, children}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-medium transition',
      active
        ? 'border-indigo-600 bg-indigo-600 text-white'
        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50',
    )}
  >
    {children}
  </button>
);

const SearchResults = ({
  results,
  query,
  onClick,
  onAskAce,
}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const [selectedType, setSelectedType] = useState(null);

  // Changing the search query clears the active filter.
  useEffect(() => {
    setSelectedType(null);
  }, [query]);

  const handleSelectType = (type) => {
    setSelectedType(type);
    tracker.searchClickFilter({search: query, type: type || 'all'});
  };

  const rows = buildRows(results, shouldHideCoaching(currentUser));
  const presentTypes = TYPE_ORDER.filter((type) => rows.some((r) => r.filterType === type));

  if (rows.length === 0) {
    return (
      <div className="w-full py-10 text-center">
        <p className="text-base font-medium leading-snug text-gray-600">
          {`Sorry, we couldn't find anything for "${query}"`}
        </p>
        {onAskAce && (
          <button
            type="button"
            onClick={onAskAce}
            className="mt-2 text-base font-semibold text-indigo-600 transition hover:text-indigo-700 cursor-pointer"
          >
            Try asking Ace
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <FadeOut right left className="mb-5">
        <div className="flex gap-2">
          <FilterPill active={!selectedType} onClick={() => handleSelectType(null)}>
            All
          </FilterPill>
          {presentTypes.map((type) => (
            <FilterPill
              key={type}
              active={selectedType === type}
              onClick={() => handleSelectType(type)}
            >
              {TYPE_LABELS[type]}
            </FilterPill>
          ))}
        </div>
      </FadeOut>

      {presentTypes
        .filter((type) => !selectedType || selectedType === type)
        .map((type) => {
          const group = rows.filter((r) => r.filterType === type);
          const shown = selectedType === type ? group : group.slice(0, PREVIEW_LIMIT);
          return (
            <div key={type} className="mb-6">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {TYPE_LABELS[type]}
              </h3>
              <div>
                {shown.map(({Component, result}) => (
                  <Component
                    key={`${type}-${result.item.id ?? result.item.slug}`}
                    result={result}
                    onClick={onClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default SearchResults;
